import { observer } from 'mobx-react-lite'
import { useStore } from '../../hooks/store'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Checklist from '@repo/datalayer/store/models/Checklist'
import { useRouter } from '../../hooks/router'
import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/checkbox'
import { cn } from '@/lib/utils'
import { c } from 'node_modules/vite/dist/node/types.d-aGj9QkWt'
import ChecklistItem from '@repo/datalayer/store/models/ChecklistItem'
import Template from '@repo/datalayer/store/models/Template'
import TemplateItem from '@repo/datalayer/store/models/TemplateItem'
import { useLegacyEffect } from '../../hooks/effect'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import toast from 'react-hot-toast'
import { Edit } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export function SortableItem({ item }: { item: TemplateItem }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      data-test-id="reorder-item"
      data-item-name={item.name}
      className="cursor-grab p-3 border-b border-b-border bg-card hover:bg-muted select-none touch-none"
      style={style}
      {...attributes}
      {...listeners}
    >
      {item.name}
    </div>
  )
}

function ReorderView({
  items,
  onChange,
}: {
  items: TemplateItem[]
  onChange: (items: TemplateItem[]) => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableItem key={item.id} item={item} />
        ))}
      </SortableContext>
    </DndContext>
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over?.id)
      const newItems = arrayMove(items, oldIndex, newIndex)
      onChange(newItems)
    }
  }
}

export const TemplateReorderPage = observer(function TemplateReorderPage() {
  const store = useStore()
  const [template, setTemplate] = useState<Template | null>(null)
  const router = useRouter()
  const { id } = router.router.params as { id: string }
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    store.fetchTemplate(id).then((template) => {
      if (template) {
        setTemplate(template)
      } else {
        // something went wrong
      }
    })
  }, [])

  if (!template) return null

  return (
    <main className="max-w-[1320px] mx-auto w-full relative" ref={containerRef}>
      <div className="px-4 mt-4">
        <div className="bg-card rounded-2xl">
          <div className="flex flex-row items-center justify-between p-4">
            <div data-test-id="reorder-template-name">{template?.name}</div>
            <Button
              data-test-id="reorder-back-button"
              onClick={() => {
                router.navigateToTemplate(template.id)
              }}
              variant="default"
            >
              {t('back')}
            </Button>
          </div>
          <div className="mt-4">
            <div className="shadow-md">
              <ReorderView
                items={template.sortedItems}
                onChange={async (newItems) => {
                  template.reorderItemsLocally(newItems.map((item) => item.id))
                  await template.reorderItems({
                    '': newItems.map((item) => item.id),
                  })

                  toast.success(t('templates.reordered'))
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  )
})
